'use client';

/**
 * Account Profile Component
 * 
 * This component allows users to update their account profile.
 * It validates user inputs and displays appropriate messages for success or error states.
 * 
 * @returns {JSX.Element} The account profile component.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useForm, SubmitHandler, FieldErrors } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import { useSession } from 'next-auth/react';

// Type imports
import { User } from '@/lib/types/user';
import { Profile } from '@/lib/types/profile';
import { SyliusAddress } from '@/lib/types/SyliusAddress';
import { KeyClubProfile as KeyClubProfileType } from '@/lib/types/keyClubProfile';

// Function imports
import { getUserProfile } from '@/lib/functions/getUserProfile';
import { setUserProfile } from '@/lib/functions/setUserProfile';
import { setUserAddress } from '@/lib/functions/setUserAddress';
import { unPrefixStateCode } from '@/lib/functions/unPrefixStateCode';
import { prefixStateCode } from '@/lib/functions/prefixStateCode';
import { logMessage } from '@/lib/functions/logMessage';
import { updateUserKeyClubProfile } from '@/lib/functions/updateUserKeyClubProfile';
import { getUserKeyClubProfile } from '@/lib/functions/getUserKeyClubProfile';
import { formatPhoneInput } from '@/lib/functions/formatPhoneInput';
import { mergeUserWithKcProfile } from '@/lib/functions/mergeUserWithKcProfile';

// Component Imports
import Toast from '@/components/toast';
import StylizedButton from '@/components/stylizedButton';
import Loader from '@/components/loader';
import StylizedSelect from '@/components/stylizedSelect';

// Constants Imports
import { REGEX } from '@/lib/constants/common';
import { STATES_CODES } from '@/lib/constants/common';

// Style Imports
import styles from './accountProfile.module.scss';

// Field Labels
const LABELS = {
  emailPassword_email: 'Email',
  emailPassword_currentPassword: 'Current Password',
  emailPassword_newPassword: 'New Password',
  emailPassword_confirmNewPassword: 'Confirm New Password',
  billingContact_firstName: 'First Name',
  billingContact_lastName: 'Last Name',
  billingContact_phoneNumber: 'Phone Number',
  billingContact_company: 'Company (Optional)',
  billingContact_email: 'Email',
  billingAddress_address: 'Address',
  billingAddress_aptSuite: 'Apt/Suite (Optional)',
  billingAddress_city: 'City',
  billingAddress_state: 'State',
  billingAddress_zip: 'Zip',
  billingAddress_id: '', // hiddden field
  shippingContact_firstName: 'First Name',
  shippingContact_lastName: 'Last Name',
  shippingContact_phoneNumber: 'Phone Number',
  shippingAddress_address: 'Address',
  shippingAddress_aptSuite: 'Apt/Suite (Optional)',
  shippingAddress_city: 'City',
  shippingAddress_state: 'State',
  shippingAddress_zip: 'Zip',
  shippingAddress_id: '', // hidden field
  profile_firstName: '', // hidden field
  profile_lastName: '', // hidden field
};

// Error Messages
const ERROR_MESSAGES = {
  emailPassword_email: `Please provide a valid ${LABELS['emailPassword_email'].toLowerCase()}.`,
  emailPassword_currentPassword: `Please enter your ${LABELS['emailPassword_currentPassword'].toLowerCase()}.`,
  emailPassword_newPassword: `Please enter a valid ${LABELS['emailPassword_newPassword'].toLowerCase()}.`,
  emailPassword_confirmNewPassword: `Passwords do not match.`,
  billingContact_firstName: `Please enter the ${LABELS['billingContact_firstName'].toLowerCase()}`,
  billingContact_lastName: `Please enter the ${LABELS['billingContact_lastName'].toLowerCase()}`,
  billingContact_phoneNumber: `Please provide a valid ${LABELS['billingContact_phoneNumber'].toLowerCase()}`,
  billingContact_company: `Please provide a ${LABELS['billingContact_company'].toLowerCase()}`,
  billingContact_email: `Please provide a valid ${LABELS['billingContact_email'].toLowerCase()}`,
  billingAddress_address: `Please provide an ${LABELS['billingAddress_address'].toLowerCase()}`,
  billingAddress_aptSuite: `Please provide a ${LABELS['billingAddress_aptSuite'].toLowerCase()}`,
  billingAddress_city: `Please provide a ${LABELS['billingAddress_city'].toLowerCase()}`,
  billingAddress_state: `Please provide a ${LABELS['billingAddress_state'].toLowerCase()}`,
  billingAddress_zip: `Please provide a valid ${LABELS['billingAddress_zip'].toLowerCase()}`,
  billingAddress_id: '', // hidden field
  shippingContact_firstName: `Please enter the ${LABELS['shippingContact_firstName'].toLowerCase()}`,
  shippingContact_lastName: `Please enter the ${LABELS['shippingContact_lastName'].toLowerCase()}`,
  shippingContact_phoneNumber: `Please provide a valid ${LABELS['shippingContact_phoneNumber'].toLowerCase()}`,
  shippingAddress_address: `Please provide an ${LABELS['shippingAddress_address'].toLowerCase()}`,
  shippingAddress_aptSuite: `Please provide a ${LABELS['shippingAddress_aptSuite'].toLowerCase()}`,
  shippingAddress_city: `Please provide a ${LABELS['shippingAddress_city'].toLowerCase()}`,
  shippingAddress_state: `Please provide a ${LABELS['shippingAddress_state'].toLowerCase()}`,
  shippingAddress_zip: `Please provide a valid ${LABELS['shippingAddress_zip'].toLowerCase()}`,
  updateSuccess: 'Your profile has been successfully updated.',
  updateError: 'An error occurred while updating your profile.',
  missingFieldsError: 'The following required fields are missing: ',
  invalidFieldsError: 'The following fields are invalid: ',
  shippingAddress_id: '', // hidden field
  profile_firstName: '', // hidden field
  profile_lastName: '', // hidden field
};

// Field validations
const VALIDATIONS = {
  emailPassword_email: {
    required: true,
    pattern: {
      value: REGEX.EMAIL,
      message: ERROR_MESSAGES['emailPassword_email'],
    },
  },
  emailPassword_currentPassword: { required: false },
  emailPassword_newPassword: {
    required: false,
    pattern: {
      value: REGEX.PASSWORD,
      message: ERROR_MESSAGES['emailPassword_newPassword'],
    }
  },
  emailPassword_confirmNewPassword: (newPassword: string) => ({
    required: false,
    validate: (value: string) => value === newPassword || ERROR_MESSAGES['emailPassword_confirmNewPassword'],
  }),
  billingContact_firstName: {
    required: true,
    pattern: {
      value: REGEX.NAME_LATIN_1,
      message: ERROR_MESSAGES['billingContact_firstName'],
    },
  },
  billingContact_lastName: {
    required: true,
    pattern: {
      value: REGEX.NAME_LATIN_1,
      message: ERROR_MESSAGES['billingContact_lastName'],
    },
  },
  billingContact_phoneNumber: {
    required: true,
    pattern: {
      value: REGEX.PHONE,
      message: ERROR_MESSAGES['billingContact_phoneNumber'],
    },
  },
  billingContact_company: {
    required: false,
    pattern: {
      value: REGEX.ORDER_NOTES,
      message: ERROR_MESSAGES['billingContact_company'],
    },
  },
  billingContact_email: {
    required: true,
    pattern: {
      value: REGEX.EMAIL,
      message: ERROR_MESSAGES['billingContact_email'],
    },
  },
  billingAddress_address: {
    required: true,
    pattern: {
      value: REGEX.POSTAL_ADDRESS,
      message: ERROR_MESSAGES['billingAddress_address'],
    },
  },
  billingAddress_aptSuite: {
    required: false,
    pattern: {
      value: REGEX.POSTAL_ADDRESS,
      message: ERROR_MESSAGES['billingAddress_aptSuite'],
    },
  },
  billingAddress_city: {
    required: true,
    pattern: {
      value: REGEX.POSTAL_CITY,
      message: ERROR_MESSAGES['billingAddress_city'],
    },
  },
  billingAddress_state: { required: true },
  billingAddress_zip: {
    required: true,
    pattern: {
      value: REGEX.POSTAL_CODE,
      message: ERROR_MESSAGES['billingAddress_zip'],
    },
  },
  shippingContact_firstName: {
    required: true,
    pattern: {
      value: REGEX.NAME_LATIN_1,
      message: ERROR_MESSAGES['shippingContact_firstName'],
    },
  },
  shippingContact_lastName: {
    required: true,
    pattern: {
      value: REGEX.NAME_LATIN_1,
      message: ERROR_MESSAGES['shippingContact_lastName'],
    },
  },
  shippingContact_phoneNumber: {
    required: true,
    pattern: {
      value: REGEX.PHONE,
      message: ERROR_MESSAGES['shippingContact_phoneNumber'],
    },
  },
  shippingAddress_address: {
    required: true,
    pattern: {
      value: REGEX.POSTAL_ADDRESS,
      message: ERROR_MESSAGES['shippingAddress_address'],
    },
  },
  shippingAddress_aptSuite: {
    required: false,
    pattern: {
      value: REGEX.POSTAL_ADDRESS,
      message: ERROR_MESSAGES['shippingAddress_aptSuite'],
    },
  },
  shippingAddress_city: {
    required: true,
    pattern: {
      value: REGEX.POSTAL_CITY,
      message: ERROR_MESSAGES['shippingAddress_city'],
    },
  },
  shippingAddress_state: { required: true },
  shippingAddress_zip: {
    required: true,
    pattern: {
      value: REGEX.POSTAL_CODE,
      message: ERROR_MESSAGES['shippingAddress_zip'],
    },
  },
};

type Inputs = {
  // Email & password
  emailPassword_email: string;
  emailPassword_currentPassword: string;
  emailPassword_newPassword: string;
  emailPassword_confirmNewPassword: string;

  // Billing contact & address
  billingContact_firstName: string;
  billingContact_lastName: string;
  billingContact_phoneNumber: string;
  billingContact_company: string;
  billingContact_email: string;
  billingAddress_address: string;
  billingAddress_aptSuite: string;
  billingAddress_city: string;
  billingAddress_state: string;
  billingAddress_zip: string;
  billingAddress_id: number;

  // Shipping contact & address
  shippingContact_firstName: string;
  shippingContact_lastName: string;
  shippingContact_phoneNumber: string;
  shippingAddress_address: string;
  shippingAddress_aptSuite: string;
  shippingAddress_city: string;
  shippingAddress_state: string;
  shippingAddress_zip: string;
  shippingAddress_id: number;

  // Profile (hidden for now)
  profile_firstName: string;
  profile_lastName: string;
};

export default function AccountProfile() {
  // State
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [billingStateCode, setBillingStateCode] = useState<string>("TX");
  const [shippingStateCode, setShippingStateCode] = useState<string>("TX");
  // debug profile errors
  const [updateErrorMessage, setUpdateErrorMessage] = useState<string>('');

  const { data: session, status, update } = useSession();
  const [profile, setProfile] = useState<User | undefined>(undefined);

  const retryCountRef = useRef(3);

  const {
    register,
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<Inputs>();

  const extractErrorMessage = (err: unknown): string => {
    try {
      if (!err) return 'Unknown error';
      if (typeof err === 'string') return err;

      const errObj = err as Record<string, unknown>;
      const response = errObj.response as { data?: unknown } | undefined;
      if (response?.data) {
        const d = response.data;
        if (typeof d === 'string') return d;
        if (typeof d === 'object' && d !== null) {
          const data = d as Record<string, unknown>;
          if (typeof data.error === 'string') return data.error;
          if (typeof data.message === 'string') return data.message;
          if (data.errors) return JSON.stringify(data.errors);
          return JSON.stringify(d);
        }
      }

      if (errObj.data) {
        const d = errObj.data;
        if (typeof d === 'string') return d;
        if (typeof d === 'object' && d !== null) {
          const data = d as Record<string, unknown>;
          if (typeof data.error === 'string') return data.error;
          if (typeof data.message === 'string') return data.message;
          if (data.errors) return JSON.stringify(data.errors);
        }
      }

      if (typeof errObj.error === 'string') return errObj.error;
      if (typeof errObj.message === 'string') return errObj.message;

      return JSON.stringify(err);
    } catch {
      return 'Unknown error';
    }
  };

  const newPassword = watch('emailPassword_newPassword');

  const validateField = (
    fieldName: keyof Inputs,
    label: string,
    errors: FieldErrors<Inputs>,
    missing: string[],
    invalid: string[]
  ) => {
    if (errors[fieldName]?.type === 'required') {
      missing.push(label);
    } else if (errors[fieldName]) {
      invalid.push(label);
    }
  };

  const validateFields = (errors: FieldErrors<Inputs>) => {
    const missing: string[] = [];
    const invalid: string[] = [];

    validateField('emailPassword_email', LABELS['emailPassword_email'], errors, missing, invalid);
    validateField('emailPassword_currentPassword', LABELS['emailPassword_currentPassword'], errors, missing, invalid);
    validateField('emailPassword_newPassword', LABELS['emailPassword_newPassword'], errors, missing, invalid);
    validateField('emailPassword_confirmNewPassword', LABELS['emailPassword_confirmNewPassword'], errors, missing, invalid);

    validateField('billingContact_firstName', `Billing ${LABELS['billingContact_firstName']}`, errors, missing, invalid);
    validateField('billingContact_lastName', `Billing ${LABELS['billingContact_lastName']}`, errors, missing, invalid);
    validateField('billingContact_phoneNumber', `Billing ${LABELS['billingContact_phoneNumber']}`, errors, missing, invalid);
    validateField('billingContact_company', `Billing ${LABELS['billingContact_company']}`, errors, missing, invalid);
    validateField('billingContact_email', `Billing ${LABELS['billingContact_email']}`, errors, missing, invalid);

    validateField('billingAddress_address', `Billing ${LABELS['billingAddress_address']}`, errors, missing, invalid);
    validateField('billingAddress_aptSuite', `Billing ${LABELS['billingAddress_aptSuite']}`, errors, missing, invalid);
    validateField('billingAddress_city', `Billing ${LABELS['billingAddress_city']}`, errors, missing, invalid);
    validateField('billingAddress_state', `Billing ${LABELS['billingAddress_state']}`, errors, missing, invalid);
    validateField('billingAddress_zip', `Billing ${LABELS['billingAddress_zip']}`, errors, missing, invalid);

    validateField('shippingContact_firstName', `Shipping ${LABELS['shippingContact_firstName']}`, errors, missing, invalid);
    validateField('shippingContact_lastName', `Shipping ${LABELS['shippingContact_lastName']}`, errors, missing, invalid);
    validateField('shippingContact_phoneNumber', `Shipping ${LABELS['shippingContact_phoneNumber']}`, errors, missing, invalid);

    validateField('shippingAddress_address', `Shipping ${LABELS['shippingAddress_address']}`, errors, missing, invalid);
    validateField('shippingAddress_aptSuite', `Shipping ${LABELS['shippingAddress_aptSuite']}`, errors, missing, invalid);
    validateField('shippingAddress_city', `Shipping ${LABELS['shippingAddress_city']}`, errors, missing, invalid);
    validateField('shippingAddress_state', `Shipping ${LABELS['shippingAddress_state']}`, errors, missing, invalid);
    validateField('shippingAddress_zip', `Shipping ${LABELS['shippingAddress_zip']}`, errors, missing, invalid);

    setMissingFields(missing);
    setInvalidFields(invalid);
  };

  // Reset any current errors and clear any existing toasts
  const resetErrorsAndToasts = () => {
    setMissingFields([]);
    setInvalidFields([]);
    setUpdateSuccess(false);
    setUpdateError(false);
    setUpdateErrorMessage('');
  };

  const onUpdate: SubmitHandler<Inputs> = async (formData) => {
    resetErrorsAndToasts();
    setLoading(true);

    const billingPhoneDigits = formatPhoneInput(formData.billingContact_phoneNumber, 'digits');
    const shippingPhoneDigits = formatPhoneInput(formData.shippingContact_phoneNumber, 'digits');

    try {
      const billingAddress: SyliusAddress = {
        firstName: formData?.billingContact_firstName,
        lastName: formData?.billingContact_lastName,
        phoneNumber: billingPhoneDigits || '',
        company: formData?.billingContact_company,
        email: formData?.billingContact_email,
        street: formData?.billingAddress_address,
        street2: formData?.billingAddress_aptSuite,
        city: formData?.billingAddress_city,
        provinceCode: prefixStateCode(billingStateCode),
        postcode: formData?.billingAddress_zip,
        countryCode: 'US',
      };
      const billingAddressResp = await setUserAddress(
        formData?.billingAddress_id,
        billingAddress,
        session?.token);
      const billingAddressId = billingAddressResp?.data?.id;
      formData.billingAddress_id = billingAddressId;

      // now update the Shipping Address
      const shippingAddress: SyliusAddress = {
        firstName: formData?.shippingContact_firstName,
        lastName: formData?.shippingContact_lastName,
        phoneNumber: shippingPhoneDigits || '',
        street: formData?.shippingAddress_address,
        street2: formData?.shippingAddress_aptSuite,
        city: formData?.shippingAddress_city,
        provinceCode: prefixStateCode(shippingStateCode),
        postcode: formData?.shippingAddress_zip,
        countryCode: 'US',
      };
      const shippingAddressResp = await setUserAddress(
        formData?.shippingAddress_id,
        shippingAddress,
        session?.token);
      const shippingAddressId = shippingAddressResp?.data?.id;
      formData.shippingAddress_id = shippingAddressId;

      // finally, update the profile itself
      const profilePayload: Partial<Profile> = {
        email: formData.emailPassword_email,
        firstName: formData.billingContact_firstName,
        lastName: formData.billingContact_lastName,
        ...(formData.emailPassword_currentPassword ? { currentPassword: formData.emailPassword_currentPassword } : {}),
        ...(formData.emailPassword_newPassword ? { newPassword: formData.emailPassword_newPassword } : {}),
        billingAddress: formData.billingAddress_id
          ? `/api/v2/shop/addresses/${formData.billingAddress_id}`
          : undefined,
        shippingAddress: formData.shippingAddress_id
          ? `/api/v2/shop/addresses/${formData.shippingAddress_id}`
          : undefined,
      };

      const profileResp = await setUserProfile(session?.user?.customerId, session?.token, profilePayload);

      const keyClubNumber = session?.user?.keyClubNumber;
      let kcProfileResp: KeyClubProfileType | null = null;

      if (keyClubNumber) {
        // KC API expects all fields in the update payload; missing fields may be nulled.
        const kcProfile = await getUserKeyClubProfile(keyClubNumber).catch(() => null);

        const kcUpdatedProfile: Partial<KeyClubProfileType> = {
          ...(kcProfile ?? {}),
          first_name: formData.billingContact_firstName,
          last_name: formData.billingContact_lastName,
          address_1: formData.billingAddress_address,
          address_2: formData.billingAddress_aptSuite,
          city: formData.billingAddress_city,
          state: billingStateCode,
          zip: formData.billingAddress_zip,
          primary_phone: billingPhoneDigits || '',
        };
        kcProfileResp = await updateUserKeyClubProfile(keyClubNumber, kcUpdatedProfile);
      }

      const kcUpdateSucceeded = !keyClubNumber || kcProfileResp;
      if (profileResp?.success && billingAddressResp?.success && shippingAddressResp?.success && kcUpdateSucceeded) {
        clearErrors();
        setUpdateSuccess(true);
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('accountProfileUpdateSuccess', '1');
        }
        if (session?.user) {
          await update({
            user: {
              firstName: formData.billingContact_firstName,
              lastName: formData.billingContact_lastName,
              fullName: `${formData.billingContact_firstName} ${formData.billingContact_lastName}`,
              billingAddress,
              shippingAddress,
            },
          });
        }
        setProfile(undefined);
      } else {
        const fieldErrors: string[] = [];

        if (!billingAddressResp?.success) {
          fieldErrors.push(`Billing address: ${extractErrorMessage(billingAddressResp)}`);
        }
        if (!shippingAddressResp?.success) {
          fieldErrors.push(`Shipping address: ${extractErrorMessage(shippingAddressResp)}`);
        }
        if (!profileResp?.success) {
          fieldErrors.push(`Profile: ${extractErrorMessage(profileResp)}`);
        }
        const kcFailed =
          keyClubNumber &&
          (!kcProfileResp ||
            (typeof kcProfileResp === 'object' &&
              kcProfileResp !== null &&
              'success' in kcProfileResp &&
              (kcProfileResp as { success?: boolean }).success === false));

        if (kcFailed) {
          fieldErrors.push(`Key Club: ${extractErrorMessage(kcProfileResp)}`);
        }

        setUpdateErrorMessage(fieldErrors.join(' | ') || 'Unknown error');
        setUpdateError(true);
      }
    } catch (error) {
      setUpdateErrorMessage(extractErrorMessage(error));
      setUpdateError(true);
    } finally {
      setLoading(false);
    }
  };

  const onError = (errors: FieldErrors<Inputs>) => {
    resetErrorsAndToasts();
    validateFields(errors);
  };

  const renderFormField = (
    id: keyof Inputs,
    registerOptions: object,
    type: string = 'text',
    autocomplete: string,
    required: boolean = false,
  ) => {
    const errorId = uuidv4();

    return (
      <div className="formInput formText">
        <label htmlFor={id}>
          {LABELS[id]}
          &nbsp;
          {required && <span>*</span>}
        </label>
        <input
          className={errors[id] ? styles.fieldError : ''}
          id={id}
          type={type}
          aria-invalid={errors[id] ? 'true' : 'false'}
          aria-describedby={errors[id] ? errorId : undefined}
          {...register(id, registerOptions)}
          disabled={loading || id === 'emailPassword_email'} // changing email disabled for MVP
          autoComplete={autocomplete}
          pattern={type === 'tel' ? REGEX.PHONE.source : undefined}
        />
        {errors[id] && (
          <span id={errorId} className={styles.errorMessage}>
            {ERROR_MESSAGES[id]}
          </span>
        )}
      </div>
    );
  };

  const renderSelectField = (
    id: keyof Inputs,
    options: { label: string, value: string | number }[],
    defaultValue: string,
    onChange: (value: string | number) => void,
    required: boolean = false,
  ) => (
    <div className={`formInput formSelect ${errors[id] ? 'formError' : ''}`} key={id}>
      <label htmlFor={id}>
        {LABELS[id]}
        &nbsp;
        {required && <span>*</span>}
      </label>
      <StylizedSelect
        name={id}
        styles={styles}
        options={options}
        onChange={onChange}
        defaultValue={defaultValue}
      />
      {errors[id] && <span id={`${id}-error`} className={styles.error}>{ERROR_MESSAGES[id]}</span>}
    </div>
  );

  useEffect(() => {
    if (profile) {
      reset({
        emailPassword_email: profile.email || '',
        emailPassword_currentPassword: '', // Empty, as you don't want to prefill passwords
        emailPassword_newPassword: '',
        emailPassword_confirmNewPassword: '',
        billingContact_firstName: profile.billingAddress?.firstName || '',
        billingContact_lastName: profile.billingAddress?.lastName || '',
        billingContact_phoneNumber: profile.billingAddress?.phoneNumber || '',
        billingContact_company: profile.billingAddress?.company || '',
        billingContact_email: profile.billingAddress?.email || profile.email || '',
        billingAddress_address: profile.billingAddress?.street || '',
        billingAddress_aptSuite: profile.billingAddress?.street2 || '',
        billingAddress_city: profile.billingAddress?.city || '',
        billingAddress_state: unPrefixStateCode(profile.billingAddress?.provinceCode) || 'TX',
        billingAddress_zip: profile.billingAddress?.postcode || '',
        billingAddress_id: profile.billingAddress?.id || undefined,
        shippingContact_firstName: profile.shippingAddress?.firstName || '',
        shippingContact_lastName: profile.shippingAddress?.lastName || '',
        shippingContact_phoneNumber: profile.shippingAddress?.phoneNumber || '',
        shippingAddress_address: profile.shippingAddress?.street || '',
        shippingAddress_aptSuite: profile.shippingAddress?.street2 || '',
        shippingAddress_city: profile.shippingAddress?.city || '',
        shippingAddress_state: unPrefixStateCode(profile.shippingAddress?.provinceCode) || 'TX',
        shippingAddress_zip: profile.shippingAddress?.postcode || '',
        shippingAddress_id: profile.shippingAddress?.id || undefined,
        // hidden fields
        profile_firstName: profile.firstName || '',
        profile_lastName: profile.lastName || '',
      });
      setBillingStateCode(unPrefixStateCode(profile.billingAddress?.provinceCode) || 'TX');
      setShippingStateCode(unPrefixStateCode(profile.shippingAddress?.provinceCode) || 'TX');
    }
  }, [profile, reset]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateSuccessFlag = sessionStorage.getItem('accountProfileUpdateSuccess');
      if (updateSuccessFlag) {
        setUpdateSuccess(true);
        sessionStorage.removeItem('accountProfileUpdateSuccess');
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.token || !session?.user?.customerId) {
        return;
      }

      setLoading(true);
      try {
        const [profileResp, fetchedKcProfile] = await Promise.all([
          getUserProfile(session.user.customerId, session.token),
          session.user.keyClubNumber
            ? getUserKeyClubProfile(session.user.keyClubNumber).catch(() => null)
            : Promise.resolve(null),
        ]);

        setProfile(mergeUserWithKcProfile(profileResp?.data as User, fetchedKcProfile));

        if (profileResp?.success && profileResp?.data) {
          retryCountRef.current = 3;
        } else {
          retryCountRef.current -= 1;
        }
      } catch (error) {
        retryCountRef.current -= 1;
        logMessage(`Failed to fetch profile: ${error}`, 'error');
      } finally {
        setLoading(false);
      }
    };

    if (!profile && !loading && retryCountRef.current > 0) {
      fetchData();
    }
  }, [session, loading, profile]);

  // show the loader
  if (status === 'loading') {
    return <Loader variant="2" />;
  }

  return (
    <div className={styles.accountProfile}>
      {updateSuccess && (
        <div aria-live="polite">
          <Toast id={uuidv4()} type="success" label={true} message={ERROR_MESSAGES['updateSuccess']} dismissible={true} />
        </div>
      )}

      <form className={styles.form} onSubmit={handleSubmit(onUpdate, onError)} noValidate>

        {/* Email & password section ------------------------------------------------ */}
        <div className={styles.module}>
          <h2>Email & Password</h2>
          <div className={styles.inputWrapper}>
            {renderFormField(
              'emailPassword_email',
              VALIDATIONS['emailPassword_email'],
              'email',
              'email',
              VALIDATIONS['emailPassword_email'].required,
            )}
            {renderFormField(
              'emailPassword_currentPassword',
              VALIDATIONS['emailPassword_currentPassword'],
              'password',
              'current-password',
              VALIDATIONS['emailPassword_currentPassword'].required,
            )}
            {renderFormField(
              'emailPassword_newPassword',
              VALIDATIONS['emailPassword_newPassword'],
              'password',
              'new-password',
              VALIDATIONS['emailPassword_newPassword'].required,
            )}
            {renderFormField(
              'emailPassword_confirmNewPassword',
              VALIDATIONS['emailPassword_confirmNewPassword'](newPassword),
              'password',
              'confirm-new-password',
              VALIDATIONS['emailPassword_confirmNewPassword'](newPassword).required,
            )}
          </div>
        </div>

        {/* Billing contact section ----------------------------------------------- */}
        <div className={classNames(styles.module, styles.billingContact)}>
          <h2>Billing Contact</h2>
          <div className={styles.inputWrapper}>
            {renderFormField(
              'billingContact_firstName',
              VALIDATIONS['billingContact_firstName'],
              'text',
              'first-name',
              VALIDATIONS['billingContact_firstName'].required,
            )}
            {renderFormField(
              'billingContact_lastName',
              VALIDATIONS['billingContact_lastName'],
              'text',
              'last-name',
              VALIDATIONS['billingContact_lastName'].required,
            )}
            {renderFormField(
              'billingContact_phoneNumber',
              VALIDATIONS['billingContact_phoneNumber'],
              'tel',
              'phone',
              VALIDATIONS['billingContact_phoneNumber'].required,
            )}
            {renderFormField(
              'billingContact_company',
              VALIDATIONS['billingContact_company'],
              'text',
              'company',
              VALIDATIONS['billingContact_company'].required,
            )}
            {renderFormField(
              'billingContact_email',
              VALIDATIONS['billingContact_email'],
              'email',
              'email',
              VALIDATIONS['billingContact_email'].required,
            )}
          </div>
        </div>

        {/* Shipping contact section ------------------------------------------------ */}
        <div className={classNames(styles.module, styles.shippingContact)}>
          <h2>Shipping Contact</h2>
          <div className={styles.inputWrapper}>
            {renderFormField(
              'shippingContact_firstName',
              VALIDATIONS['shippingContact_firstName'],
              'text',
              'first-name',
              VALIDATIONS['shippingContact_firstName'].required,
            )}
            {renderFormField(
              'shippingContact_lastName',
              VALIDATIONS['shippingContact_lastName'],
              'text',
              'last-name',
              VALIDATIONS['shippingContact_lastName'].required,
            )}
            {renderFormField(
              'shippingContact_phoneNumber',
              VALIDATIONS['shippingContact_phoneNumber'],
              'tel',
              'phone',
              VALIDATIONS['shippingContact_phoneNumber'].required,
            )}
          </div>
        </div>

        {/* Billing address section ------------------------------------------------- */}
        <div className={classNames(styles.module, styles.addressModule)}>
          <h2>Billing Address</h2>
          <div className={styles.inputWrapper}>
            {renderFormField(
              'billingAddress_address',
              VALIDATIONS['billingAddress_address'],
              'text',
              'address',
              VALIDATIONS['billingAddress_address'].required,
            )}
            {renderFormField(
              'billingAddress_aptSuite',
              VALIDATIONS['billingAddress_aptSuite'],
              'text',
              'apt-suite',
              VALIDATIONS['billingAddress_aptSuite'].required,
            )}
            {renderFormField(
              'billingAddress_city',
              VALIDATIONS['billingAddress_city'],
              'text',
              'city',
              VALIDATIONS['billingAddress_city'].required,
            )}
            <div className={styles.stateZipContainer}>
              {renderSelectField(
                'billingAddress_state',
                STATES_CODES,
                billingStateCode,
                (value: string | number) => setBillingStateCode(value as string),
                VALIDATIONS['billingAddress_state'].required,
              )}
              {renderFormField(
                'billingAddress_zip',
                VALIDATIONS['billingAddress_zip'],
                'text',
                'zip',
                VALIDATIONS['billingAddress_zip'].required,
              )}
            </div>
          </div>
        </div>

        {/* Shipping address section ------------------------------------------------ */}
        <div className={classNames(styles.module, styles.addressModule)}>
          <h2>Shipping Address</h2>
          <div className={styles.inputWrapper}>
            {renderFormField(
              'shippingAddress_address',
              VALIDATIONS['shippingAddress_address'],
              'text',
              'address',
              VALIDATIONS['shippingAddress_address'].required,
            )}
            {renderFormField(
              'shippingAddress_aptSuite',
              VALIDATIONS['shippingAddress_aptSuite'],
              'text',
              'apt-suite',
              VALIDATIONS['shippingAddress_aptSuite'].required,
            )}
            {renderFormField(
              'shippingAddress_city',
              VALIDATIONS['shippingAddress_city'],
              'text',
              'city',
              VALIDATIONS['shippingAddress_city'].required,
            )}
            <div className={styles.stateZipContainer}>
              {renderSelectField(
                'shippingAddress_state',
                STATES_CODES,
                shippingStateCode,
                (value: string | number) => setShippingStateCode(value as string),
                // 'State',
                VALIDATIONS['shippingAddress_state'].required,
              )}
              {renderFormField(
                'shippingAddress_zip',
                VALIDATIONS['shippingAddress_zip'],
                'text',
                'zip',
                VALIDATIONS['shippingAddress_zip'].required,
              )}
            </div>
          </div>
        </div>

        {/* Submit button section --------------------------------------------------- */}
        < div className={classNames(styles.module, styles.fullWidth)} >
          <StylizedButton
            submitType='submit'
            variant='primary'
            size='large'
            disabled={loading}
          >
            Save Profile
          </StylizedButton>
          {loading && <Loader variant='2' />}
        </div >
      </form>

      {missingFields.length > 0 && (
        <div role="alert" aria-atomic="true">
          <Toast id={uuidv4()} type="error" label={true} message={`${ERROR_MESSAGES['missingFieldsError']} <em>${missingFields.join(', ')}</em>`} />
        </div>
      )}
      {invalidFields.length > 0 && (
        <div role="alert" aria-atomic="true">
          <Toast id={uuidv4()} type="error" label={true} message={`${ERROR_MESSAGES['invalidFieldsError']} <em>${invalidFields.join(', ')}</em>`} />
        </div>
      )}
      {updateError && (
        <div role="alert" aria-atomic="true">
          <Toast id={uuidv4()} type="error" label={true} message={`${ERROR_MESSAGES['updateError']}${updateErrorMessage ? ` <em>${updateErrorMessage}</em>` : ''}`} />
        </div>
      )}
    </div>
  );
}

